import { useCallback, useEffect, useRef, useState } from 'react';

import { confirmDiscardChanges } from './unsaved.ts';

export type ViewName = 'board' | 'tickets' | 'review' | 'graph' | 'agents' | 'reports' | 'ops' | 'runs' | 'budgets' | 'settings' | 'help';

/**
 * Views that no longer exist, and where their links go instead.
 *
 * Deleted production/ops screens land on Board — that is the surviving default workspace.
 * Plugins and automation already redirected to Ops (which still exists), so those bookmarks
 * keep that successor rather than being bounced twice.
 */
const RETIRED_VIEWS: Readonly<Record<string, ViewName>> = {
  overview: 'board',
  attention: 'board',
  inbox: 'board',
  trace: 'board',
  builds: 'board',
  playtests: 'board',
  engine: 'board',
  milestones: 'board',
  design: 'board',
  history: 'board',
  evidence: 'board',
  setup: 'board',
  plugins: 'ops',
  automation: 'ops',
};

export const VIEWS: ViewName[] = [
  'board',
  'tickets',
  'review',
  'graph',
  'agents',
  'reports',
  'ops',
  'runs',
  'budgets',
  'settings',
  'help',
];

export interface Route {
  view: ViewName;
  /**
   * Project slug when the URL is project-scoped (`/p/{slug}/board`).
   * Null on the unscoped form (`/board`, `/ops`, …), which is also how
   * "all projects" is written. The live store may still remember a slug for
   * unscoped bookmarks; the URL wins when it names one.
   */
  project: string | null;
  /** Ticket key when the detail drawer is open — it is a URL, so it is linkable. */
  ticket: string | null;
  /** Stage run id when the drawer should open straight onto a transcript. */
  run: number | null;
  runLane?: 'attempt' | 'sidecar';
  /** Catalog id selected on the budgets hub. */
  budget: string | null;
  /**
   * Linkable board toolbar state: grouping, sort and filter text. Absent means the
   * default, so an untouched board keeps a clean URL. On Settings, `group` is the
   * open config group.
   */
  group: string | null;
  sort: string | null;
  q: string | null;
  /**
   * The ticket drawer's open tab.
   *
   * `?ticket=` made the drawer linkable, which was right and half the job: the tab was not,
   * so "look at the transcript on MONO-3" could not be sent to anyone, a reload dropped back
   * to Timeline, and a screenshot of the Evidence tab could not be reproduced from its URL.
   */
  drawer: string | null;
  /** Runs view outcome filter. Reload-safe and shareable: an incident view is worth sending. */
  outcome: string | null;
  /** Board card density. Was local-only, so a screenshot could not be reproduced. */
  density: string | null;
  /** Board and Tickets milestone filter (`?milestone=`). Null is every milestone in the scoped project. */
  milestone: string | null;
  /** Board and Tickets phase filter (`?phase=`, a phase id). Kept wherever the milestone filter is. */
  phase?: string | null;
  /**
   * Tickets index state filter (`?state=Waiting`). On Runs, the ticket state the
   * attempt ran in (`?state=Implementing`).
   */
  state: string | null;
  /** Tickets index waiting-reason filter (`?reason=human_clarification`). */
  reason: string | null;
  /** Runs view attempt-status filter (`?status=Failed`). */
  status: string | null;
  /** Runs view harness filter (`?harness=claude`). */
  harness: string | null;
  /** Graph-only shareable filters and arrangement mode. Geometry deliberately stays local. */
  graphPreset?: string | null;
  graphCategory?: string | null;
  graphMode?: string | null;
}

/**
 * Whether a path names a screen that has been retired, and nothing else.
 *
 * `parseRoute` already lands a retired path on its successor, but the address bar keeps the old
 * URL — which then gets copied, bookmarked, and written back out by every later `replace`. This
 * is what lets the shell rewrite it once on arrival.
 */
export function isRetiredPath(pathname: string): boolean {
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/').filter((segment) => segment.length > 0);
  const first = segments[0] ?? '';
  const viewRaw = first === 'p' && segments[1] ? (segments[2] ?? '') : first;
  return viewRaw in RETIRED_VIEWS;
}

export function parseRoute(pathname: string, search = ''): Route {
  const path = pathname.replace(/^\/+|\/+$/g, '');
  const segments = path.split('/').filter((segment) => segment.length > 0);
  const first = segments[0] ?? '';

  let project: string | null = null;
  let viewRaw = first;
  if (first === 'p' && segments[1]) {
    try {
      project = decodeURIComponent(segments[1]);
    } catch {
      project = segments[1] ?? null;
    }
    if (project !== null && project.length === 0) project = null;
    viewRaw = segments[2] ?? 'board';
  }

  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const view = (VIEWS as string[]).includes(viewRaw)
    ? (viewRaw as ViewName)
    : (RETIRED_VIEWS[viewRaw] ?? 'board');
  const ticket = params.get('ticket');
  const budget = params.get('id');
  const runRaw = params.get('run');
  const run = runRaw !== null && /^\d+$/.test(runRaw) ? Number(runRaw) : null;
  const group = params.get('group');
  const sort = params.get('sort');
  const q = params.get('q');
  const drawer = params.get('drawer');
  const outcome = params.get('outcome');
  const density = params.get('density');
  const milestone = params.get('milestone');
  const phase = params.get('phase');
  const state = params.get('state');
  const reason = params.get('reason');
  const status = params.get('status');
  const harness = params.get('harness');
  const graphPreset = params.get('preset');
  const graphCategory = params.get('category');
  const graphMode = params.get('mode');
  return {
    view,
    project,
    ticket: ticket && ticket.length > 0 ? ticket : null,
    run,
    runLane: params.get('runLane') === 'sidecar' ? 'sidecar' : 'attempt',
    budget: view === 'budgets' && budget && budget.length > 0 ? budget : null,
    /*
     * `group` means the board's grouping and, on Settings, which config group is open. Both
     * are "which slice of this screen am I looking at", and Settings needed one so a warning
     * or a doc could link straight to the field it is talking about.
     */
    group: (view === 'board' || view === 'settings' || view === 'budgets') && group && group.length > 0 ? group : null,
    sort: view === 'board' && sort && sort.length > 0 ? sort : null,
    q: (view === 'board' || view === 'tickets' || view === 'graph') && q && q.length > 0 ? q : null,
    // The drawer can be open over any view, so its tab is not scoped to one.
    drawer: drawer && drawer.length > 0 ? drawer : null,
    outcome: view === 'runs' && outcome && outcome.length > 0 ? outcome : null,
    density: view === 'board' && density && density.length > 0 ? density : null,
    milestone:
      (view === 'board' || view === 'tickets' || view === 'graph') && milestone && milestone.length > 0 ? milestone : null,
    phase: (view === 'board' || view === 'tickets' || view === 'graph') && phase && phase.length > 0 ? phase : null,
    state: (view === 'tickets' || view === 'runs' || view === 'graph') && state && state.length > 0 ? state : null,
    reason: view === 'tickets' && reason && reason.length > 0 ? reason : null,
    status: view === 'runs' && status && status.length > 0 ? status : null,
    harness: view === 'runs' && harness && harness.length > 0 ? harness : null,
    graphPreset: view === 'graph' && graphPreset && graphPreset.length > 0 ? graphPreset : null,
    graphCategory: view === 'graph' && graphCategory && graphCategory.length > 0 ? graphCategory : null,
    graphMode: view === 'graph' && graphMode && graphMode.length > 0 ? graphMode : null,
  };
}

export function routeUrl(route: Route): string {
  const params = new URLSearchParams();
  if (route.ticket) params.set('ticket', route.ticket);
  if (route.run !== null) {
    params.set('run', String(route.run));
    if (route.runLane === 'sidecar') params.set('runLane', 'sidecar');
  }
  if (route.view === 'budgets' && route.budget) params.set('id', route.budget);
  if (route.view === 'budgets' && route.group) params.set('group', route.group);
  if (route.drawer) params.set('drawer', route.drawer);
  if (route.view === 'board') {
    if (route.group) params.set('group', route.group);
    if (route.sort) params.set('sort', route.sort);
    if (route.q) params.set('q', route.q);
    if (route.density) params.set('density', route.density);
    if (route.milestone) params.set('milestone', route.milestone);
    if (route.phase) params.set('phase', route.phase);
  }
  if (route.view === 'tickets') {
    if (route.state) params.set('state', route.state);
    if (route.reason) params.set('reason', route.reason);
    if (route.q) params.set('q', route.q);
    if (route.milestone) params.set('milestone', route.milestone);
    if (route.phase) params.set('phase', route.phase);
  }
  if (route.view === 'graph') {
    if (route.graphPreset) params.set('preset', route.graphPreset);
    if (route.state) params.set('state', route.state);
    if (route.q) params.set('q', route.q);
    if (route.milestone) params.set('milestone', route.milestone);
    if (route.phase) params.set('phase', route.phase);
    if (route.graphCategory) params.set('category', route.graphCategory);
    if (route.graphMode) params.set('mode', route.graphMode);
  }
  if (route.view === 'settings' && route.group) params.set('group', route.group);
  if (route.view === 'runs') {
    if (route.outcome) params.set('outcome', route.outcome);
    if (route.state) params.set('state', route.state);
    if (route.status) params.set('status', route.status);
    if (route.harness) params.set('harness', route.harness);
  }
  const query = params.toString();
  const prefix =
    route.project && route.project.length > 0
      ? `/p/${encodeURIComponent(route.project)}/${route.view}`
      : `/${route.view}`;
  return `${prefix}${query ? `?${query}` : ''}`;
}

function parse(): Route {
  return parseRoute(window.location.pathname, window.location.search);
}

function toUrl(route: Route): string {
  return routeUrl(route);
}

/**
 * Does moving from `current` to `next` unmount an editor?
 *
 * The view, the project and the ticket drawer (open, closed, or on another tab) each carry
 * their own editors; so does the settings group, which is what the Settings side navigation
 * changes. Board toolbar state (grouping, sort, filters, density) does not,
 * and a dirty ticket drawer over the board must not be asked about a filter change.
 */
export function leavesEditor(current: Route, next: Route): boolean {
  if (current.view !== next.view) return true;
  if (current.project !== next.project) return true;
  if (current.ticket !== next.ticket) return true;
  if (current.drawer !== next.drawer) return true;
  if (current.view === 'settings' && current.group !== next.group) return true;
  return false;
}

/**
 * Routing, in forty lines.
 *
 * A router library would be more code than the whole feature: production views, an
 * Operations group that keeps every existing operator URL, retired paths that land on Board,
 * optional `/p/{slug}/` project scope, and one
 * modal. What genuinely matters is that
 * the *drawer* lives in the URL — an operator who finds something worth showing someone
 * should be able to send `/p/gridguard/board?ticket=GRID-6` and have it open on the
 * same thing, and the back button should close the drawer rather than leaving the app.
 * Unscoped `/board?ticket=GRID-6` bookmarks still parse.
 */
export function useRoute(): {
  route: Route;
  go: (view: ViewName, extras?: Partial<Route>) => void;
  /** `tab` opens the drawer on that tab, such as `Review`. */
  openTicket: (key: string, run?: number, project?: string | null, tab?: string | null) => void;
  closeTicket: () => void;
  /** `confirmed` skips the unsaved-edit prompt when the caller already asked. */
  replace: (next: Partial<Route>, options?: { confirmed?: boolean }) => void;
} {
  const [route, setRoute] = useState<Route>(() => parse());
  const routeRef = useRef(route);

  useEffect(() => {
    routeRef.current = route;
  }, [route]);

  /*
   * A retired URL is rewritten once, on arrival.
   *
   * Landing the view is only half the job: an operator who follows an old `/playtests` link and
   * then copies the address bar hands somebody else a link to a screen that does not exist.
   * `replaceState`, not a push, so the back button does not have to step through the redirect.
   */
  useEffect(() => {
    if (!isRetiredPath(window.location.pathname)) return;
    window.history.replaceState(null, '', toUrl(routeRef.current));
  }, []);

  useEffect(() => {
    const onPop = () => {
      const next = parse();
      routeRef.current = next;
      setRoute(next);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /*
   * Every navigation that unmounts an editor asks first. The editors register themselves in
   * `lib/unsaved.ts`; this is the one place the question is asked for view changes, project
   * changes, drawer tabs, closing the drawer and settings groups alike, so no surface has to
   * remember to ask on its own.
   */
  const push = useCallback((next: Route) => {
    if (leavesEditor(parse(), next) && !confirmDiscardChanges()) return;
    window.history.pushState(null, '', toUrl(next));
    routeRef.current = next;
    setRoute(next);
  }, []);

  const go = useCallback(
    (view: ViewName, extras?: Partial<Route>) => {
      // Changing view closes the drawer: its content is a ticket, and carrying it across to
      // Settings would be a modal about nothing. The selected project stays in the URL.
      const current = parse();
      const keepMilestone = view === 'board' || view === 'tickets' || view === 'graph';
      push({
        view,
        project: extras?.project !== undefined ? extras.project : current.project,
        ticket: null,
        run: null,
        budget: null,
        // Board constraints belong to the board. Leaving it drops them; coming back to it
        // from a link keeps whatever that link carried. Tickets keeps milestone (top-bar
        // scope) and takes state/reason/q from extras so Waiting rail rows can deep-link.
        group: view === 'board' ? current.group : null,
        sort: view === 'board' ? current.sort : null,
        q: view === 'tickets' || view === 'graph' ? (extras?.q ?? null) : view === 'board' ? current.q : null,
        density: view === 'board' ? current.density : null,
        milestone: keepMilestone ? current.milestone : null,
        phase: keepMilestone ? current.phase : null,
        state:
          view === 'tickets'
            ? (extras?.state ?? null)
            : view === 'graph'
              ? (extras?.state ?? null)
              : view === 'runs'
              ? (extras?.state ?? (current.view === 'runs' ? current.state : null))
              : null,
        reason: view === 'tickets' ? (extras?.reason ?? null) : null,
        status: view === 'runs' ? (extras?.status ?? (current.view === 'runs' ? current.status : null)) : null,
        harness:
          view === 'runs' ? (extras?.harness ?? (current.view === 'runs' ? current.harness : null)) : null,
        graphPreset: view === 'graph' ? (extras?.graphPreset ?? null) : null,
        graphCategory: view === 'graph' ? (extras?.graphCategory ?? null) : null,
        graphMode: view === 'graph' ? (extras?.graphMode ?? null) : null,
        drawer: null,
        outcome: view === 'runs' ? current.outcome : null,
      });
    },
    [push],
  );

  const openTicket = useCallback(
    (key: string, run?: number, project?: string | null, tab?: string | null) => {
      const current = parse();
      push({
        view: current.view,
        project: project === undefined ? current.project : project,
        ticket: key,
        run: run ?? null,
        runLane: 'attempt',
        budget: current.budget,
        group: current.group,
        sort: current.sort,
        q: current.q,
        density: current.density,
        milestone: current.milestone,
        phase: current.phase,
        state: current.state,
        reason: current.reason,
        status: current.status,
        harness: current.harness,
        graphPreset: current.graphPreset,
        graphCategory: current.graphCategory,
        graphMode: current.graphMode,
        outcome: current.outcome,
        // Opening a ticket from a link that named a tab keeps it; opening one from a card
        // starts on the drawer's own default rather than inheriting the last tab looked at.
        // A link that names a *run* wants the Activity tab, where the drawer renders that
        // attempt's transcript above the ticket timeline.
        drawer: tab ?? (run !== undefined ? 'Activity' : null),
      });
    },
    [push],
  );

  const closeTicket = useCallback(() => {
    const current = parse();
    if (current.ticket === null) return;
    if (!confirmDiscardChanges()) return;
    // Back, not push, so Escape and the browser's back button agree with each other.
    window.history.back();
  }, []);

  const replace = useCallback((next: Partial<Route>, options?: { confirmed?: boolean }) => {
    const current = parse();
    const merged = { ...current, ...next };
    if (!options?.confirmed && leavesEditor(current, merged) && !confirmDiscardChanges()) return;
    window.history.replaceState(null, '', toUrl(merged));
    setRoute(merged);
  }, []);

  return { route, go, openTicket, closeTicket, replace };
}

export const VIEW_LABEL: Record<ViewName, string> = {
  board: 'Board',
  tickets: 'Tickets',
  review: 'Review queue',
  graph: 'Graph',
  agents: 'Agents',
  reports: 'Reports',
  ops: 'Operations',
  runs: 'Runs',
  budgets: 'Budgets',
  settings: 'Settings',
  help: 'Help',
};
